<template>
  <div id="rg-playback-controller" v-if="isDateRangeValid">
    <div class="playback-progressbar">
      <vue-slider
        class="playback-progressbar-box"
        ref="rgPlaybackSlider"
        v-model="currentTime"
        :min="dateRange.startTime"
        :max="dateRange.endTime"
        :interval="1"
        :tooltip-formatter="tooltipFormatter"
        :duration="0"
        @change="handleChange"
        @drag-start="handleSeekStart"
        @drag-end="handleSeekEnd"
      />
    </div>
    <div class="ctl-panel">
      <div class="ctl-left">
        <span class="material-icons play-icon" @click="handlePlay">
          {{ iconName }}
        </span>
        <div class="speed-wrapper" @click.stop="toggleSpeedMenu">
          <span class="speed-btn"> {{ speedLabel }} </span>
          <div class="speed-popup" v-if="showSpeedMenu">
            <div class="speed-header">再生速度</div>
            <div
              class="speed-option"
              v-for="speed in speedList"
              :key="speed"
              @click.stop="setSpeed(speed)"
            >
              {{ speed }}x
            </div>
          </div>
        </div>
        <span class="time-label">
          {{ formattedCurrentTime }}
        </span>
        <span
          class="material-icons calendar-icon"
          @click="$emit('openDateDialog')"
        >
          event
        </span>
      </div>
      <div class="ctl-right">
        <div class="layer-filter-buttons">
          <layer-visibility-button
            :layerId="layerIds.myVessels"
            label="自船"
            @change="setLayerVisibility"
          />
          <layer-visibility-button
            :layerId="layerIds.otherVessels"
            label="一般船"
            @change="setLayerVisibility"
          />
          <layer-visibility-button
            :layerId="layerIds.rgObject"
            label="その他"
            @change="setLayerVisibility"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/antd.css";
import moment from "moment-timezone";
import LayerVisibilityButton from "./components/LayerVisibilityButton";
import C from "../../Constants";

export default {
  name: "RgPlaybackController",
  components: { VueSlider, LayerVisibilityButton },
  props: {
    nowTime: { type: Number, default: 0 },
    dateRange: {
      type: Object,
      default: () => ({}), // null防止
    },
    playbackStatus: String,
  },
  data() {
    return {
      currentTime: (this.dateRange && this.dateRange.startTime) || 0,
      speedList: [1, 2, 4, 8, 16, 32, 64],
      speedIndex: 0,
      layerIds: {
        myVessels: C.LAYER_ID.RG_MY_VESSEL_LAYER,
        otherVessels: C.LAYER_ID.RG_OTHER_VESSEL_LAYER,
        rgObject: C.LAYER_ID.RG_OBJECT_LAYER,
      },
      endTimeFetched: 0,
      playSpeed: 1,
      showSpeedMenu: false,
    };
  },
  watch: {
    nowTime(val) {
      if (val !== this.currentTime) {
        this.currentTime = val;
      }
    },
    dateRange: {
      immediate: true,
      handler(range) {
        if (range && this.currentTime < range.startTime) {
          this.currentTime = range.startTime;
        }
      },
    },
  },
  computed: {
    isDateRangeValid() {
      if (!this.dateRange) {
        return false;
      }
      return (
        this.dateRange &&
        typeof this.dateRange.startTime === "number" &&
        typeof this.dateRange.endTime === "number" &&
        this.dateRange.startTime < this.dateRange.endTime
      );
    },
    tooltipFormatter() {
      return val =>
        moment
          .unix(val)
          .tz("Asia/Tokyo")
          .format("YYYY/MM/DD HH:mm:ss");
    },
    iconName() {
      switch (this.playbackStatus) {
        case "play":
          return "pause";
        case "pause":
          return "play_arrow";
        default:
          return "replay";
      }
    },
    formattedTime() {
      const offsetSec = this.currentTime - this.dateRange.startTime;
      const duration = moment.duration(offsetSec, "seconds");

      const h = duration.hours();
      const m = duration.minutes();
      const s = duration.seconds();

      if (duration.asMinutes() >= 60) {
        return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(
          2,
          "0"
        )}`;
      } else {
        return `${m}:${String(s).padStart(2, "0")}`;
      }
    },
    formattedCurrentTime() {
      return moment
        .unix(this.currentTime)
        .tz("Asia/Tokyo")
        .format("YYYY/MM/DD HH:mm:ss");
    },
    speedLabel() {
      return `${this.speedList[this.speedIndex]}x`;
    },
  },
  methods: {
    handleChange(val) {
      this.currentTime = val;
      this.$emit("onSeekDragEnd", this.currentTime);
    },
    handleSeekStart() {
      this.$emit("onSeekDragStart");
    },
    handleSeekEnd() {
      this.$emit("onSeekDragEnd", this.currentTime);
    },
    handlePlay() {
      if (this.playbackStatus === "play") {
        this.$emit("pause");
      } else if (this.playbackStatus === "pause") {
        this.$emit("play");
      } else {
        this.$emit("replay");
      }
    },
    handleOpenSpeedSelector() {
      this.showSpeedMenu = !this.showSpeedMenu;
    },
    toggleSpeedMenu() {
      this.showSpeedMenu = !this.showSpeedMenu;
    },
    setSpeed(speed) {
      const index = this.speedList.indexOf(speed);
      if (index !== -1) {
        this.speedIndex = index;
        this.$emit("onPlaySpeedChange", speed);
      }
      this.showSpeedMenu = false;
    },
    setLayerVisibility(state) {
      this.$emit("onLayerVisibilityChange", state);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

#rg-playback-controller {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  z-index: 200;
  width: 50vw;
  height: 80px;
  padding: 16px;
  background-color: rgba(51, 51, 51, 0.7);
  border-radius: 5px;

  .ctl-panel {
    display: flex;
    justify-content: space-between;

    .ctl-left {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      gap: 6px;
    }

    .ctl-right {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
    }
  }

  .play-icon {
    color: $aisea-white;
    font-size: 40px;
    cursor: pointer;
  }

  .time-label {
    color: $aisea-white;
    font-variant-numeric: tabular-nums;
    width: 180px;
    display: inline-block;
    text-align: center;
  }

  .speed-icon {
    color: $aisea-white;
  }

  .speed-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;

    .speed-btn {
      color: white;
      font-size: 14px;
      padding: 4px 8px;
      border-radius: 4px;
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .speed-popup {
      position: absolute;
      bottom: 100%;
      left: 0;
      background: #2c2c2c;
      border: 1px solid #555;
      border-radius: 4px;
      padding: 4px 0;
      z-index: 10;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      min-width: 80px;
      margin-bottom: 6px;

      .speed-header {
        padding: 6px 12px;
        font-size: 13px;
        font-weight: bold;
        color: #aaa;
        border-bottom: 1px solid #444;
      }
    }

    .speed-option {
      padding: 6px 12px;
      color: white;
      font-size: 14px;
      white-space: nowrap;

      &:hover {
        background-color: #4d9ef2;
      }
    }
  }

  .layer-filter-buttons {
    display: flex;
    gap: 8px;
  }

  .time-label-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .calendar-icon {
    font-size: 24px;
    cursor: pointer;
    color: #ffffff;
  }
  .calendar-icon:hover {
    opacity: 0.7;
  }

  .speed-icon {
    font-size: 24px;
    cursor: pointer;
    color: #ffffff;
  }
  .speed-icon:hover {
    opacity: 0.7;
  }
}
</style>
